import React, { useEffect, useRef, useState } from 'react';
import type { NavItem } from '@/types';
import styles from './MagazineNav.module.css';

interface MagazineNavProps {
  items: NavItem[];
}

interface LinkClipPath {
  [key: string]: {
    red: string;
    white: string;
  };
}

export const MagazineNav: React.FC<MagazineNavProps> = ({ items }) => {
  const navRef = useRef<HTMLElement>(null);
  const redLayerRef = useRef<HTMLDivElement>(null);
  const [linkClipPaths, setLinkClipPaths] = useState<LinkClipPath>({});

  useEffect(() => {
    const handleScroll = () => {
      if (!navRef.current || !redLayerRef.current) return;

      // Get all red/dark background elements
      const heroSection = document.getElementById('cover');
      const statsSection = document.getElementById('stats');
      const contactSection = document.getElementById('contact');
      const quoteBoxElements = document.querySelectorAll('[class*="quoteBox"]');
      const portfolioSection = document.getElementById('portfolio');
      const portfolioItems = portfolioSection?.querySelectorAll('[class*="portfolioItem"]') || [];

      // Collect all dark background elements
      const darkElements: Element[] = [];
      if (heroSection) darkElements.push(heroSection);
      if (statsSection) darkElements.push(statsSection);
      if (contactSection) darkElements.push(contactSection);
      quoteBoxElements.forEach(el => darkElements.push(el));
      portfolioItems.forEach(el => darkElements.push(el));

      // Get all navigation links in the red layer (they define positions)
      const navLinks = redLayerRef.current.querySelectorAll('a');
      const newClipPaths: LinkClipPath = {};

      navLinks.forEach((link, index) => {
        const linkRect = link.getBoundingClientRect();
        const linkId = items[index].id;
        const linkHeight = linkRect.height;
        const linkTop = linkRect.top;

        // Collect Y-ranges where this link overlaps with dark backgrounds
        const darkRanges: Array<{ start: number; end: number }> = [];

        for (const darkElement of darkElements) {
          const darkRect = darkElement.getBoundingClientRect();

          // Check for X-axis overlap (horizontal overlap)
          const overlapX = !(linkRect.right < darkRect.left || linkRect.left > darkRect.right);

          if (overlapX) {
            // Calculate Y-axis overlap (vertical overlap) relative to this link
            const overlapStart = Math.max(0, darkRect.top - linkTop);
            const overlapEnd = Math.min(linkHeight, darkRect.bottom - linkTop);

            if (overlapStart < overlapEnd) {
              // Convert to percentages relative to link height
              darkRanges.push({
                start: (overlapStart / linkHeight) * 100,
                end: (overlapEnd / linkHeight) * 100
              });
            }
          }
        }

        // Create clip-path for this specific link
        if (darkRanges.length > 0) {
          // Merge overlapping ranges
          darkRanges.sort((a, b) => a.start - b.start);
          const merged: Array<{ start: number; end: number }> = [darkRanges[0]];

          for (let i = 1; i < darkRanges.length; i++) {
            const current = darkRanges[i];
            const last = merged[merged.length - 1];

            if (current.start <= last.end) {
              last.end = Math.max(last.end, current.end);
            } else {
              merged.push(current);
            }
          }

          // Use first merged range for clip-path (gradual Y-axis clipping)
          const firstRange = merged[0];
          const whiteClip = `inset(${firstRange.start}% 0% ${100 - firstRange.end}% 0%)`;

          newClipPaths[linkId] = {
            white: whiteClip,  // Show white text where overlap exists
            red: 'inset(0% 0% 0% 0%)'  // Red always visible as base
          };
        } else {
          // No overlap with dark backgrounds - show only red text
          newClipPaths[linkId] = {
            white: 'inset(100% 0% 0% 0%)', // Hidden
            red: 'inset(0% 0% 0% 0%)'      // Visible
          };
        }
      });

      setLinkClipPaths(newClipPaths);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [items]);

  return (
    <nav ref={navRef} className={styles.magazineNav}>
      <div className={styles.navContainer}>
        {/* Red text layer - for light backgrounds */}
        <div ref={redLayerRef} className={styles.navLayerRed}>
          {items.map((item) => {
            const IconComponent = item.iconComponent;
            return (
              <a
                key={`red-${item.id}`}
                href={item.href}
                className={styles.navLink}
                style={{
                  clipPath: linkClipPaths[item.id]?.red || 'inset(0% 0% 0% 0%)',
                  transition: 'clip-path 0.15s ease-out'
                }}
              >
                {IconComponent && <IconComponent className={styles.navIcon} />}
                <span className={styles.navLabel}>{item.label}</span>
              </a>
            );
          })}
        </div>

        {/* White text layer - for dark/red backgrounds */}
        <div className={styles.navLayerWhite}>
          {items.map((item) => {
            const IconComponent = item.iconComponent;
            const isVisible = linkClipPaths[item.id]?.white && linkClipPaths[item.id]?.white !== 'inset(100% 0% 0% 0%)';
            return (
              <a
                key={`white-${item.id}`}
                href={item.href}
                className={styles.navLink}
                style={{
                  clipPath: linkClipPaths[item.id]?.white || 'inset(100% 0% 0% 0%)',
                  transition: 'clip-path 0.15s ease-out',
                  pointerEvents: isVisible ? 'auto' : 'none'
                }}
              >
                {IconComponent && <IconComponent className={styles.navIcon} />}
                <span className={styles.navLabel}>{item.label}</span>
              </a>
            );
          })}
        </div>
      </div>
    </nav>
  );
};
